if (!customElements.get('featured-collection')) {
    customElements.define(
        'featured-collection',
        class FeaturedCollection extends HTMLElement {
            constructor() {
                super();
            }

            connectedCallback() {
                this.selectors = {
                    sliderWrapper: '.featured-collection__items',
                    nextEl: '.swiper-button-next',
                    prevEl: '.swiper-button-prev',
                };
                this.classes = {
                    grid: 'f-grid',
                    swiper: 'swiper',
                    swiperWrapper: 'swiper-wrapper',
                };

                this.sectionId = this.dataset.sectionId;
                this.section = this.closest(`.section-${this.sectionId}`);
                this.sliderWrapper = this.querySelector(this.selectors.sliderWrapper);

                this.enableSlider = this.dataset.enableSlider === 'true';
                this.enableSwipeMobile = this.hasAttribute('data-mobile-items');
                this.items = parseInt(this.dataset.items);
                this.tabletItems = parseInt(this.dataset.tabletItems);

                this.sliderInstance = false;
                this._autoSlideTimer = null;
                // Safe no-op so destroySlider() can always call this
                this._stopAutoSlide = () => {
                    if (this._autoSlideTimer) {
                        clearInterval(this._autoSlideTimer);
                        this._autoSlideTimer = null;
                    }
                };

                // Only exit if NEITHER desktop slider NOR mobile swipe is enabled
                if (!this.enableSlider && !this.enableSwipeMobile) return;

                if (!this.sliderWrapper) return;

                const mql = window.matchMedia(FoxTheme.config.mediaQueryMobile || '(max-width: 767px)');
                // Speed fix: start the slider only when the section is near the viewport.
                // Avoids a layout shift + main-thread work during page load; features unchanged.
                const start = () => {
                    if (this._sliderStarted) return;
                    this._sliderStarted = true;
                    mql.onchange = this.init.bind(this);
                    this.init();
                };
                if ('IntersectionObserver' in window) {
                    const io = new IntersectionObserver((entries) => {
                        if (entries.some((e) => e.isIntersecting)) {
                            io.disconnect();
                            start();
                        }
                    }, { rootMargin: '600px 0px' });
                    io.observe(this);
                } else {
                    start();
                }
            }

            init() {
                this.destroySlider();

                const isMobile = window.matchMedia(FoxTheme.config.mediaQueryMobile || '(max-width: 767px)').matches;

                if (isMobile) {
                    if (this.enableSwipeMobile) {
                        this.initSlider();
                    }
                    // else: native CSS swipe (swipe-mobile classes restored by destroySlider)
                } else {
                    if (this.enableSlider) {
                        this.initSlider();
                    }
                }
            }

            initSlider() {
                if (typeof this.sliderInstance === 'object') return;

                const columnGap = FoxTheme.utils.getGridColumnGap(this.sliderWrapper);
                const actualItemCount = this.sliderWrapper.children.length;

                // Don't init with an empty grid — Swiper would measure 0px widths
                if (actualItemCount === 0) return;

                const desktopItems = parseInt(this.items) || 4;
                const mobileItems = parseInt(this.dataset.mobileItems) || 1;
                const isMobile = window.matchMedia(FoxTheme.config.mediaQueryMobile || '(max-width: 767px)').matches;

                // Loop: only enable if explicitly 'true' and enough products (>= so equal count also loops)
                const canLoopDesktop = this.dataset.loopDesktop === 'true' && actualItemCount >= desktopItems;
                const canLoopMobile = this.dataset.loopMobile === 'true' && actualItemCount >= mobileItems;
                const currentLoop = isMobile ? canLoopMobile : canLoopDesktop;

                // Auto-slide: enabled separately per device
                const isAutoSlide = isMobile
                    ? (this.dataset.autoSlide === 'true' && actualItemCount >= mobileItems)
                    : (this.dataset.autoSlideDesktop === 'true' && actualItemCount >= desktopItems);

                const speedDesktop = (parseInt(this.dataset.autoSlideDesktopSpeed) || 3) * 1000;
                const speedMobile = (parseInt(this.dataset.autoSlideSpeed) || 3) * 1000;
                const autoplayDelay = isMobile ? speedMobile : speedDesktop;

                if (isMobile) {
                    // Mobile Swiper: single-column or multi-column
                    this.sliderOptions = {
                        slidesPerView: mobileItems,
                        spaceBetween: columnGap.mobile || 15,
                        navigation: {
                            nextEl: this.section ? this.section.querySelector(this.selectors.nextEl) : null,
                            prevEl: this.section ? this.section.querySelector(this.selectors.prevEl) : null,
                        },
                        loop: currentLoop,
                        threshold: 2,
                    };

                    // Remove native CSS mobile swipe classes so Swiper takes control
                    this.classList.remove('swipe-mobile', `swipe-mobile--${mobileItems}-cols`);
                    this.sliderWrapper.classList.remove('swipe-mobile__inner');
                } else {
                    // Desktop / tablet Swiper
                    this.sliderOptions = {
                        slidesPerView: this.tabletItems > 3 ? 3 : (parseInt(this.tabletItems) || 3),
                        spaceBetween: columnGap.tablet,
                        navigation: {
                            nextEl: this.section ? this.section.querySelector(this.selectors.nextEl) : null,
                            prevEl: this.section ? this.section.querySelector(this.selectors.prevEl) : null,
                        },
                        pagination: false,
                        breakpoints: {
                            1024: {
                                slidesPerView: desktopItems > 4 ? 4 : desktopItems,
                                spaceBetween: columnGap.tabletLarge,
                            },
                            1280: {
                                slidesPerView: desktopItems,
                                spaceBetween: columnGap.desktop,
                            },
                        },
                        loop: currentLoop,
                        threshold: 2,
                    };
                }

                this.classList.add(this.classes.swiper);
                this.sliderWrapper.classList.remove(this.classes.grid);
                this.sliderWrapper.classList.add(this.classes.swiperWrapper);

                this.sliderInstance = new window.FoxTheme.Carousel(this, this.sliderOptions);
                this.sliderInstance.init();

                // ── Manual auto-slide via setInterval ──────────────────────────────
                // Reliable regardless of what FoxTheme.Carousel forwards to Swiper.
                this._stopAutoSlide();

                if (isAutoSlide) {
                    this._startAutoSlide = () => {
                        this._stopAutoSlide();
                        this._autoSlideTimer = setInterval(() => {
                            const swiper = this.sliderInstance && this.sliderInstance.slider;
                            if (!swiper) return;
                            // Stop when reaching the end if loop is off
                            if (swiper.isEnd && !currentLoop) {
                                this._stopAutoSlide();
                            } else {
                                swiper.slideNext();
                            }
                        }, autoplayDelay);
                    };

                    this._stopAutoSlide = () => {
                        if (this._autoSlideTimer) {
                            clearInterval(this._autoSlideTimer);
                            this._autoSlideTimer = null;
                        }
                    };

                    this._startAutoSlide();

                    // Pause on hover (desktop) and on touch (mobile)
                    this._boundStop = () => this._stopAutoSlide();
                    this._boundStart = () => this._startAutoSlide();
                    this.addEventListener('mouseenter', this._boundStop);
                    this.addEventListener('mouseleave', this._boundStart);
                    this.addEventListener('touchstart', this._boundStop, { passive: true });
                    this.addEventListener('touchend', this._boundStart, { passive: true });
                }

                this.fixQuickviewDuplicate();
                this.calcNavButtonsPosition();
                this.calcHeadingColumnHeight();

                window.addEventListener('resize', FoxTheme.utils.debounce(() => {
                    this.calcNavButtonsPosition();
                    this.calcHeadingColumnHeight();
                }, 100));
            }

            destroySlider() {
                // Stop auto-slide timer and remove listeners
                if (this._autoSlideTimer) {
                    clearInterval(this._autoSlideTimer);
                    this._autoSlideTimer = null;
                }
                if (this._boundStop) {
                    this.removeEventListener('mouseenter', this._boundStop);
                    this.removeEventListener('mouseleave', this._boundStart);
                    this.removeEventListener('touchstart', this._boundStop);
                    this.removeEventListener('touchend', this._boundStart);
                    this._boundStop = null;
                    this._boundStart = null;
                }

                this.classList.remove(this.classes.swiper);
                if (this.sliderWrapper) {
                    this.sliderWrapper.classList.remove(this.classes.swiperWrapper);
                    this.sliderWrapper.classList.add(this.classes.grid);
                }

                // Restore native CSS mobile swipe classes if reverting on mobile
                const isMobile = window.matchMedia(FoxTheme.config.mediaQueryMobile || '(max-width: 767px)').matches;
                if (isMobile && this.dataset.mobileItems) {
                    this.classList.add('swipe-mobile', `swipe-mobile--${this.dataset.mobileItems}-cols`);
                    if (this.sliderWrapper) this.sliderWrapper.classList.add('swipe-mobile__inner');
                }

                if (typeof this.sliderInstance !== 'object') return;
                try {
                    this.sliderInstance.slider.destroy();
                } catch (e) {
                    // Broken Swiper state — safe to ignore
                }
                this.sliderInstance = false;
            }

            calcNavButtonsPosition() {
                if (this.dataset.calcButtonPosition !== 'true') return;

                const firstMedia = this.querySelector('.product-card__image-wrapper');
                if (firstMedia && firstMedia.clientHeight > 0 && this.section) {
                    this.section.style.setProperty(
                        '--swiper-navigation-top-offset',
                        parseInt(firstMedia.clientHeight) / 2 + 'px'
                    );
                }
            }

            calcHeadingColumnHeight() {
                if (this.dataset.calcHeadingColumnHeight !== 'true') return;

                const firstMedia = this.querySelector('.product-card__image-wrapper');
                if (firstMedia && firstMedia.clientHeight > 0 && this.section) {
                    this.section.style.setProperty('--heading-column-height', firstMedia.clientHeight + 'px');
                }
            }

            fixQuickviewDuplicate() {
                let modalIds = [];
                Array.from(this.querySelectorAll('quick-view-modal')).forEach((modal) => {
                    const modalID = modal.getAttribute('id');
                    if (modalIds.includes(modalID)) {
                        modal.remove();
                    } else {
                        modalIds.push(modalID);
                    }
                });
            }
        }
    );
}
